import {
  MeterFlowVolume,
  useCreateMeterFlowVolumeMutation,
  useDeleteMeterFlowVolumeMutation,
  useUpdateMeterFlowVolumeMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseMeterFlowVolumeMutationProps = {}

export type UseMeterFlowVolumeMutationResult = {
  createMeterFlowVolume: (productionVolume: Partial<MeterFlowVolume>, onSuccess: (() => void) | undefined) => void
  updateMeterFlowVolume: (productionVolume: Partial<MeterFlowVolume>, onSuccess: (() => void) | undefined) => void
  deleteMeterFlowVolume: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useMeterFlowVolumeMutation = (props: UseMeterFlowVolumeMutationProps = {}): UseMeterFlowVolumeMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createMeterFlowVolume, {isLoading: isCreating}] = useCreateMeterFlowVolumeMutation()
  const [_updateMeterFlowVolume, {isLoading: isUpdating}] = useUpdateMeterFlowVolumeMutation()
  const [_deleteMeterFlowVolume, {isLoading: isDeleting}] = useDeleteMeterFlowVolumeMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useMeterFlowVolumeUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.meterflowvolume", "Meter Flow Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMeterFlowVolumeUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useMeterFlowVolumeUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.meterflowvolume", "Meter Flow Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMeterFlowVolumeUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useMeterFlowVolumeUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.meterflowvolume", "Meter Flow Volume"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMeterFlowVolumeUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.meterflowvolume", "Meter Flow Volume") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.meterflowvolume", "Meter Flow Volume") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.meterflowvolume", "Meter Flow Volume") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteMeterFlowVolume({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteMeterFlowVolume, handleDeleteResult])

  const createMeterFlowVolume = useCallback((productionVolume: Partial<MeterFlowVolume>, onSuccess: (() => void) | undefined) => {
    const promise = _createMeterFlowVolume(productionVolume).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createMeterFlowVolume, handleCreateResult])

  const updateMeterFlowVolume = useCallback((productionVolume: Partial<MeterFlowVolume>, onSuccess: (() => void) | undefined) => {
    const promise = _updateMeterFlowVolume(productionVolume).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateMeterFlowVolume, handleUpdateResult])

  const deleteMeterFlowVolume = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.meterflowvolume", "Meter Flow Volume"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletemeterflowvolume.confirm", "Are you sure you want to delete this meter flow volume?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createMeterFlowVolume: createMeterFlowVolume,
    updateMeterFlowVolume: updateMeterFlowVolume,
    deleteMeterFlowVolume: deleteMeterFlowVolume,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
